// src/utils/sseReconnect.ts
// ────────────────────────────────────────────────────────────────────────────
// Shared SSE reconnection helpers with exponential backoff + jitter.
//
// Prevents thundering-herd reconnects when multiple browser tabs or SSE
// streams simultaneously lose their connections (e.g. server restart,
// load balancer rotation, Cloud Run cold-start).
//
// H3 — Security hardening: deterministic reconnect timers cause all clients
// to hit the server at the exact same instant, which can cascade into a
// self-inflicted DDoS.  Adding jitter spreads the retry window.
// ────────────────────────────────────────────────────────────────────────────

import { exponentialPlusJitter, type AdditiveJitterOptions } from './retry';

export type SseBackoffOptions = AdditiveJitterOptions;

/**
 * Calculate the next reconnect delay with exponential backoff + jitter.
 * Thin wrapper over the shared `exponentialPlusJitter` policy so every
 * reconnect/retry curve across the FE is governed by ONE primitive.
 *
 * Defaults preserved verbatim (base=1000, max=30000, jitter=1500) so
 * existing call sites observe identical behaviour.
 */
export function sseReconnectDelay(attempt: number, opts?: SseBackoffOptions): number {
  return exponentialPlusJitter(attempt, {
    baseMs:   opts?.baseMs   ?? 1000,
    maxMs:    opts?.maxMs    ?? 30_000,
    jitterMs: opts?.jitterMs ?? 1500,
    rng:      opts?.rng,
  });
}

/**
 * Manage an SSE connection with automatic reconnect + jitter.
 *
 * Returns a `close()` handle the caller should invoke on unmount / cleanup.
 *
 * @param url            EventSource URL.
 * @param onSetup        Callback invoked with each new EventSource so the
 *                       caller can attach event listeners.  Return `false`
 *                       to prevent automatic reconnection on error.
 * @param opts           Backoff tuning.
 */
export function createReconnectingEventSource(
  url: string,
  onSetup: (es: EventSource, attempt: number) => boolean | void,
  opts?: SseBackoffOptions,
): { close: () => void } {
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let current: EventSource | null = null;
  let closed = false;

  function connect() {
    if (closed) return;

    const es = new EventSource(url);
    current = es;

    // Let caller attach listeners.  If it returns false, skip auto-reconnect.
    const shouldReconnect = onSetup(es, attempt);

    es.onopen = () => {
      attempt = 0; // reset backoff on successful connection
    };

    es.onerror = () => {
      if (closed) return;
      try { es.close(); } catch {}
      current = null;

      if (shouldReconnect === false) return;

      const delay = sseReconnectDelay(attempt, opts);
      attempt++;
      timer = setTimeout(connect, delay);
    };
  }

  connect();

  return {
    close() {
      closed = true;
      if (timer) { clearTimeout(timer); timer = null; }
      if (current) { try { current.close(); } catch {} current = null; }
    },
  };
}
